import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import DataTable from '../../components/DataTable';
import ColumnConfig from '../../components/ColumnConfig';
import api from '../../services/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Trophy, Eye, DollarSign, Calendar, Users, Repeat, Clock, MapPin, Copy } from 'lucide-react';
import { formatDateOnly } from '../../utils/timezone';

const dateOpts = { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' };

// Program sort order for display
const PROGRAM_SORT_ORDER = {
  'beginners': 1,
  'beginner': 1,
  'adv beginner': 2,
  'advanced beginner': 2,
  'u11': 3,
  'u13': 4,
  'u15': 5
};

const sortPrograms = (programs) => {
  return [...programs].sort((a, b) => {
    const aOrder = PROGRAM_SORT_ORDER[a.name?.toLowerCase()] || 100;
    const bOrder = PROGRAM_SORT_ORDER[b.name?.toLowerCase()] || 100;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.name.localeCompare(b.name);
  });
};

const Programs = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [programClasses, setProgramClasses] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [_activeTab, _setActiveTab] = useState('all');
  
  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [programToDelete, setProgramToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Copy program dialog state
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [programToCopy, setProgramToCopy] = useState(null);
  const [copyTargetLocation, setCopyTargetLocation] = useState('');
  const [copyNewName, setCopyNewName] = useState('');
  const [copying, setCopying] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    program_type: 'monthly',
    location_id: '',
    // Monthly fields
    sessions_per_week: 2,
    price: 0,
    quarterly_price: 0,
    biannual_price: 0,
    // Fixed duration fields
    duration_weeks: 8,
    total_price: 0,
    start_date: '',
    end_date: '',
    // Common fields
    age_group: '',
    skill_level: 'beginner',
    max_students: 20,
    is_active: true
  });
  
  const [locations, setLocations] = useState([]);

  const fetchPrograms = async () => {
    try {
      const response = await api.get('/programs');
      setPrograms(sortPrograms(response.data));
    } catch (error) {
      toast.error('Failed to fetch programs');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchLocations = async () => {
    try {
      const response = await api.get('/locations/active');
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  useEffect(() => {
    fetchPrograms();
    fetchLocations();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      program_type: 'monthly',
      location_id: '',
      // Monthly fields
      sessions_per_week: 2,
      price: 0,
      quarterly_price: 0,
      biannual_price: 0,
      // Fixed duration fields
      duration_weeks: 8,
      total_price: 0,
      start_date: '',
      end_date: '',
      // Common fields
      age_group: '',
      skill_level: 'beginner',
      max_students: 20,
      is_active: true
    });
    setEditingProgram(null);
  };

  const handleEdit = (program) => {
    setEditingProgram(program);
    setFormData({
      name: program.name,
      description: program.description || '',
      program_type: program.program_type || 'monthly',
      location_id: program.location_id || '',
      // Monthly fields
      sessions_per_week: program.sessions_per_week || 2,
      price: program.price || 0,
      quarterly_price: program.quarterly_price || 0,
      biannual_price: program.biannual_price || 0,
      // Fixed duration fields
      duration_weeks: program.duration_weeks || 8,
      total_price: program.total_price || 0,
      start_date: program.start_date || '',
      end_date: program.end_date || '',
      // Common fields
      age_group: program.age_group || '',
      skill_level: program.skill_level || 'beginner',
      max_students: program.max_students || 20,
      is_active: program.is_active
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProgram) {
        await api.put(`/programs/${editingProgram.id}`, formData);
        toast.success('Program updated successfully');
      } else {
        await api.post('/programs', formData);
        toast.success('Program created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchPrograms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save program');
    }
  };

  const openDeleteConfirmation = (program) => {
    setProgramToDelete(program);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!programToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`/programs/${programToDelete.id}`);
      toast.success('Program deleted');
      fetchPrograms();
      setDeleteConfirmOpen(false);
      setProgramToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete program');
    } finally {
      setDeleting(false);
    }
  };
  
  const openCopyDialog = (program) => {
    setProgramToCopy(program);
    setCopyTargetLocation('');
    setCopyNewName('');
    setCopyDialogOpen(true);
  };
  
  const handleCopyProgram = async () => {
    if (!programToCopy || !copyTargetLocation) {
      toast.error('Please select a target location');
      return;
    }
    
    setCopying(true);
    try {
      await api.post('/programs/copy', {
        source_program_id: programToCopy.id,
        target_location_id: copyTargetLocation,
        new_name: copyNewName || null
      });
      toast.success('Program copied successfully');
      fetchPrograms();
      setCopyDialogOpen(false);
      setProgramToCopy(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to copy program');
    } finally {
      setCopying(false);
    }
  };

  const viewProgramDetails = async (program) => {
    setSelectedProgram(program);
    setDetailsDialog(true);
    
    try {
      const classesRes = await api.get('/classes');
      const filteredClasses = classesRes.data.filter(c => c.program_id === program.id);
      setProgramClasses(filteredClasses);
    } catch (error) {
      console.error('Failed to load classes:', error);
      setProgramClasses([]);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Program Name',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => (
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-slate-900">{row.name}</p>
            {row.numeric_id && (
              <span className="text-xs text-slate-400 font-mono">ID: {row.numeric_id}</span>
            )}
          </div>
          <p className="text-sm text-slate-500 capitalize">{row.skill_level}</p>
        </div>
      )
    },
    {
      key: 'program_type',
      label: 'Type',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'monthly', label: 'Monthly' },
        { value: 'fixed_duration', label: 'Fixed Duration' }
      ],
      render: (row) => (
        <Badge className={row.program_type === 'fixed_duration' 
          ? 'bg-amber-100 text-amber-700' 
          : 'bg-green-100 text-green-700'
        }>
          {row.program_type === 'fixed_duration' ? (
            <><Clock className="h-3 w-3 mr-1 inline" />Fixed</>
          ) : (
            <><Repeat className="h-3 w-3 mr-1 inline" />Monthly</>
          )}
        </Badge>
      )
    },
    {
      key: 'schedule',
      label: 'Schedule',
      sortable: false,
      render: (row) => {
        if (row.program_type === 'fixed_duration') {
          return (
            <div className="text-sm">
              <p>{row.duration_weeks} weeks</p>
              {row.start_date && (
                <p className="text-slate-500">
                  {formatDateOnly(row.start_date, dateOpts)} - {row.end_date ? formatDateOnly(row.end_date, dateOpts) : 'TBD'}
                </p>
              )}
            </div>
          );
        }
        return `${row.sessions_per_week || 2}x/week`;
      }
    },
    {
      key: 'price',
      label: 'Pricing',
      sortable: true,
      render: (row) => {
        if (row.program_type === 'fixed_duration') {
          return (
            <div className="text-sm">
              <span className="font-semibold text-amber-700">{cs()}{row.total_price || 0}</span>
              <span className="text-slate-400 ml-1">total</span>
            </div>
          );
        }
        return (
          <div className="text-sm">
            <span className="font-semibold text-slate-900">{cs()}{row.price}</span>
            <span className="text-slate-400 mx-1">/</span>
            <span className="text-blue-600">{cs()}{row.quarterly_price || row.price}</span>
            <span className="text-slate-400 mx-1">/</span>
            <span className="text-blue-700 font-medium">{cs()}{row.biannual_price || row.price}</span>
          </div>
        );
      }
    },
    {
      key: 'age_group',
      label: 'Age Group',
      sortable: true,
      filterable: true,
      filterType: 'text'
    },
    {
      key: 'skill_level',
      label: 'Skill Level',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'beginner', label: 'Beginner' },
        { value: 'intermediate', label: 'Intermediate' },
        { value: 'advanced', label: 'Advanced' },
        { value: 'all', label: 'All Levels' }
      ],
      render: (row) => (
        <Badge className={`capitalize ${
          row.skill_level === 'advanced' ? 'bg-purple-100 text-purple-700' :
          row.skill_level === 'intermediate' ? 'bg-blue-100 text-blue-700' :
          'bg-slate-100 text-slate-700'
        }`}>
          {row.skill_level}
        </Badge>
      )
    },
    {
      key: 'location_name',
      label: 'Location',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: locations.map(l => ({ value: l.name, label: l.name })),
      render: (row) => (
        <span className="text-sm text-slate-600">
          {row.location_name || '-'}
        </span>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' }
      ],
      render: (row) => (
        <Badge className={row.is_active ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}>
          {row.is_active ? 'Active' : 'Inactive'}
        </Badge>
      )
    }
  ];

  const actions = (row) => (
    <div className="flex items-center justify-end gap-2">
      <Button
        variant="outline"
        size="icon"
        onClick={() => viewProgramDetails(row)}
        data-testid={`view-program-${row.id}`}
      >
        <Eye className="h-4 w-4" />
      </Button>
      {locations.length > 1 && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => openCopyDialog(row)}
          title="Copy to another location"
          data-testid={`copy-program-${row.id}`}
        >
          <Copy className="h-4 w-4" />
        </Button>
      )}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => handleEdit(row)}
        data-testid={`edit-program-${row.id}`}
      >
        <Pencil className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => openDeleteConfirmation(row)}
        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
        data-testid={`delete-program-${row.id}`}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout title="Programs">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Programs">
      <div className="space-y-6" data-testid="programs-page">
        <div className="space-y-3">
          <p className="text-slate-600">Manage training programs and pricing</p>
          <div className="flex flex-wrap items-center gap-2">
            <ColumnConfig
              columns={columns}
              visibleColumns={visibleColumns}
              onColumnChange={setVisibleColumns}
              storageKey="programs-columns"
              requiredColumns={['name']}
            />
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-program-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Program
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">
                  {editingProgram ? 'Edit Program' : 'Create New Program'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Program Type Selection */}
                <div className="space-y-2">
                  <Label>Program Type</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant={formData.program_type === 'monthly' ? 'default' : 'outline'}
                      className={formData.program_type === 'monthly' ? 'bg-green-600 hover:bg-green-700' : ''}
                      onClick={() => setFormData({ ...formData, program_type: 'monthly' })}
                      data-testid="program-type-monthly"
                    >
                      <Repeat className="h-4 w-4 mr-2" />
                      Monthly
                    </Button>
                    <Button
                      type="button"
                      variant={formData.program_type === 'fixed_duration' ? 'default' : 'outline'}
                      className={formData.program_type === 'fixed_duration' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                      onClick={() => setFormData({ ...formData, program_type: 'fixed_duration' })}
                      data-testid="program-type-fixed"
                    >
                      <Clock className="h-4 w-4 mr-2" />
                      Fixed Duration
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">
                    {formData.program_type === 'monthly' 
                      ? 'Ongoing subscription with weekly classes (e.g., regular training)'
                      : 'Set duration program with one-time payment (e.g., summer camp)'
                    }
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Program Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={formData.program_type === 'fixed_duration' ? 'e.g., Summer Cricket Camp 2025' : 'e.g., Junior Cricket Training'}
                    required
                    data-testid="program-name-input"
                  />
                </div>
                
                {/* Location Selection */}
                {locations.length > 0 && (
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Select
                      value={formData.location_id || "none"}
                      onValueChange={(value) => setFormData({ ...formData, location_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger data-testid="program-location-select">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No specific location</SelectItem>
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.name} {loc.is_primary && '(Primary)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      Assign this program to a specific academy location
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Program description..."
                    rows={3}
                    data-testid="program-description-input"
                  />
                </div>

                {/* Monthly Program Fields */}
                {formData.program_type === 'monthly' && (
                  <>
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm font-medium text-green-800 mb-2">Monthly Subscription Settings</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Sessions per Week</Label>
                          <Select
                            value={String(formData.sessions_per_week)}
                            onValueChange={(value) => setFormData({ ...formData, sessions_per_week: parseInt(value) })}
                          >
                            <SelectTrigger data-testid="program-sessions-input">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1x per week</SelectItem>
                              <SelectItem value="2">2x per week</SelectItem>
                              <SelectItem value="3">3x per week</SelectItem>
                              <SelectItem value="4">4x per week</SelectItem>
                              <SelectItem value="5">5x per week</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Max Students</Label>
                          <Input
                            type="number"
                            min="1"
                            value={formData.max_students}
                            onChange={(e) => setFormData({ ...formData, max_students: parseInt(e.target.value) })}
                            required
                            data-testid="program-max-students-input"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-green-600" />
                        Pricing (Monthly / Quarterly / Bi-Annual)
                      </Label>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Monthly</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.price}
                            onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                            required
                            data-testid="program-price-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Quarterly</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.quarterly_price}
                            onChange={(e) => setFormData({ ...formData, quarterly_price: parseFloat(e.target.value) || 0 })}
                            placeholder="$/mo"
                            data-testid="program-quarterly-price-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Bi-Annual</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.biannual_price}
                            onChange={(e) => setFormData({ ...formData, biannual_price: parseFloat(e.target.value) || 0 })}
                            placeholder="$/mo"
                            data-testid="program-biannual-price-input"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Fixed Duration Program Fields */}
                {formData.program_type === 'fixed_duration' && (
                  <>
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-sm font-medium text-amber-800 mb-2">Fixed Duration Settings</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Duration (weeks)</Label>
                          <Input
                            type="number"
                            min="1"
                            value={formData.duration_weeks}
                            onChange={(e) => setFormData({ ...formData, duration_weeks: parseInt(e.target.value) || 1 })}
                            required
                            data-testid="program-duration-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Max Students</Label>
                          <Input
                            type="number"
                            min="1"
                            value={formData.max_students}
                            onChange={(e) => setFormData({ ...formData, max_students: parseInt(e.target.value) })}
                            required
                            data-testid="program-max-students-fixed-input"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-3">
                        <div className="space-y-2">
                          <Label>Start Date</Label>
                          <Input
                            type="date"
                            value={formData.start_date}
                            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                            data-testid="program-start-date-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>End Date</Label>
                          <Input
                            type="date"
                            value={formData.end_date}
                            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                            data-testid="program-end-date-input"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-amber-600" />
                        Total Price (One-time Payment)
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.total_price}
                        onChange={(e) => setFormData({ ...formData, total_price: parseFloat(e.target.value) || 0 })}
                        placeholder="Total program cost"
                        required
                        data-testid="program-total-price-input"
                      />
                      <p className="text-xs text-slate-500">
                        This is the full amount charged for the entire program duration
                      </p>
                    </div>
                  </>
                )}

                {/* Common Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Age Group</Label>
                    <Input
                      value={formData.age_group}
                      onChange={(e) => setFormData({ ...formData, age_group: e.target.value })}
                      placeholder="e.g., 8-12 years"
                      data-testid="program-age-group-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Skill Level</Label>
                    <Select
                      value={formData.skill_level}
                      onValueChange={(value) => setFormData({ ...formData, skill_level: value })}
                    >
                      <SelectTrigger data-testid="program-skill-level-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="beginner">Beginner</SelectItem>
                        <SelectItem value="intermediate">Intermediate</SelectItem>
                        <SelectItem value="advanced">Advanced</SelectItem>
                        <SelectItem value="all">All Levels</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <Label>Active Program</Label>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    data-testid="program-active-switch"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-500 hover:bg-blue-600" data-testid="save-program-btn">
                    {editingProgram ? 'Update Program' : 'Create Program'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Monthly Programs Section */}
        <Card className="border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Repeat className="h-5 w-5 text-green-600" />
              Monthly Programs
              <Badge className="bg-green-100 text-green-700 ml-2">
                {programs.filter(p => p.program_type !== 'fixed_duration').length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <DataTable
              data={programs.filter(p => p.program_type !== 'fixed_duration')}
              columns={columns}
              visibleColumns={visibleColumns}
              actions={actions}
              emptyIcon={Trophy}
              emptyMessage="No monthly programs yet."
              searchable={true}
              searchFields={['name', 'description', 'age_group', 'skill_level', 'location_name']}
              testIdPrefix="monthly-programs"
            />
          </CardContent>
        </Card>

        {/* One-Time / Fixed Duration Programs Section */}
        <Card className="border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-amber-600" />
              One-Time Programs
              <Badge className="bg-amber-100 text-amber-700 ml-2">
                {programs.filter(p => p.program_type === 'fixed_duration').length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <DataTable
              data={programs.filter(p => p.program_type === 'fixed_duration')}
              columns={columns}
              visibleColumns={visibleColumns}
              actions={actions}
              emptyIcon={Calendar}
              emptyMessage="No one-time programs (camps, clinics, etc.) yet."
              searchable={true}
              searchFields={['name', 'description', 'age_group', 'skill_level', 'location_name']}
              testIdPrefix="onetime-programs"
            />
          </CardContent>
        </Card>

        {/* Program Details Dialog */}
        <Dialog open={detailsDialog} onOpenChange={setDetailsDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Trophy className="h-5 w-5 text-amber-500" />
                Program Details
              </DialogTitle>
            </DialogHeader>
            
            {selectedProgram && (
              <div className="space-y-6 mt-4">
                {/* Basic Info */}
                <div className={`rounded-lg p-4 ${selectedProgram.program_type === 'fixed_duration' 
                  ? 'bg-gradient-to-r from-amber-50 to-orange-50' 
                  : 'bg-gradient-to-r from-green-50 to-slate-50'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={selectedProgram.program_type === 'fixed_duration' 
                          ? 'bg-amber-100 text-amber-700' 
                          : 'bg-green-100 text-green-700'
                        }>
                          {selectedProgram.program_type === 'fixed_duration' ? (
                            <><Clock className="h-3 w-3 mr-1 inline" />Fixed Duration</>
                          ) : (
                            <><Repeat className="h-3 w-3 mr-1 inline" />Monthly</>
                          )}
                        </Badge>
                      </div>
                      <h3 className="font-heading text-xl font-bold text-slate-900">
                        {selectedProgram.name}
                      </h3>
                      <p className="text-slate-600 capitalize">{selectedProgram.skill_level} Level</p>
                    </div>
                    <Badge className={selectedProgram.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                      {selectedProgram.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  {selectedProgram.description && (
                    <p className="text-slate-600 mt-2">{selectedProgram.description}</p>
                  )}
                </div>

                {/* Stats Grid - Different for each type */}
                {selectedProgram.program_type === 'fixed_duration' ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="p-4 text-center">
                        <Clock className="h-6 w-6 mx-auto mb-2 text-amber-500" />
                        <p className="text-sm text-slate-500">Duration</p>
                        <p className="font-bold">{selectedProgram.duration_weeks} weeks</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <Calendar className="h-6 w-6 mx-auto mb-2 text-blue-500" />
                        <p className="text-sm text-slate-500">Start Date</p>
                        <p className="font-bold">{selectedProgram.start_date ? formatDateOnly(selectedProgram.start_date, dateOpts) : 'TBD'}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <Users className="h-6 w-6 mx-auto mb-2 text-purple-500" />
                        <p className="text-sm text-slate-500">Max Students</p>
                        <p className="font-bold">{selectedProgram.max_students}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <Users className="h-6 w-6 mx-auto mb-2 text-green-500" />
                        <p className="text-sm text-slate-500">Age Group</p>
                        <p className="font-bold">{selectedProgram.age_group || 'All'}</p>
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="p-4 text-center">
                        <Repeat className="h-6 w-6 mx-auto mb-2 text-green-500" />
                        <p className="text-sm text-slate-500">Sessions/Week</p>
                        <p className="font-bold">{selectedProgram.sessions_per_week || 2}x</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <Calendar className="h-6 w-6 mx-auto mb-2 text-blue-500" />
                        <p className="text-sm text-slate-500">Billing</p>
                        <p className="font-bold">Recurring</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <Users className="h-6 w-6 mx-auto mb-2 text-purple-500" />
                        <p className="text-sm text-slate-500">Max Students</p>
                        <p className="font-bold">{selectedProgram.max_students}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <Users className="h-6 w-6 mx-auto mb-2 text-amber-500" />
                        <p className="text-sm text-slate-500">Age Group</p>
                        <p className="font-bold">{selectedProgram.age_group || 'All'}</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Pricing - Different for each type */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      {selectedProgram.program_type === 'fixed_duration' ? 'Program Fee' : 'Pricing Plans'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedProgram.program_type === 'fixed_duration' ? (
                      <div className="p-4 bg-amber-50 rounded-lg text-center border-2 border-amber-200">
                        <p className="text-sm text-amber-600 mb-1">Total Program Cost</p>
                        <p className="text-3xl font-bold text-amber-700">{cs()}{selectedProgram.total_price || 0}</p>
                        <p className="text-xs text-amber-500 mt-1">One-time payment for {selectedProgram.duration_weeks} weeks</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-4">
                        <div className="p-3 bg-slate-50 rounded-lg text-center">
                          <p className="text-sm text-slate-500">Monthly</p>
                          <p className="text-xl font-bold text-slate-900">{cs()}{selectedProgram.price || 0}</p>
                          <p className="text-xs text-slate-400">per month</p>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg text-center">
                          <p className="text-sm text-blue-600">Quarterly</p>
                          <p className="text-xl font-bold text-blue-700">{cs()}{selectedProgram.quarterly_price || selectedProgram.price || 0}</p>
                          <p className="text-xs text-blue-400">per month</p>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg text-center border-2 border-green-200">
                          <p className="text-sm text-green-600">Bi-Annual</p>
                          <p className="text-xl font-bold text-green-700">{cs()}{selectedProgram.biannual_price || selectedProgram.price || 0}</p>
                          <p className="text-xs text-green-400">per month (Best Value)</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Associated Classes */}
                <div>
                  <h4 className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-blue-500" />
                    Classes ({programClasses.length})
                  </h4>
                  {programClasses.length === 0 ? (
                    <p className="text-slate-500 text-sm py-4">No classes created for this program</p>
                  ) : (
                    <div className="space-y-2">
                      {programClasses.map(cls => (
                        <div key={cls.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{cls.name}</p>
                            <p className="text-sm text-slate-500">
                              {cls.schedule_day} • {cls.start_time} - {cls.end_time}
                              {cls.coach_name && ` • ${cls.coach_name}`}
                            </p>
                          </div>
                          <Badge className={cls.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                            {cls.current_enrollment}/{cls.max_capacity}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setDetailsDialog(false)}>
                    Close
                  </Button>
                  <Button onClick={() => { setDetailsDialog(false); handleEdit(selectedProgram); }}>
                    <Pencil className="h-4 w-4 mr-1" />
                    Edit Program
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Program Confirmation Dialog */}
        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Program</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{programToDelete?.name}"? 
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-program-btn"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        
        {/* Copy Program Dialog */}
        <Dialog open={copyDialogOpen} onOpenChange={setCopyDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Copy className="h-5 w-5" />
                Copy Program to Another Location
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">Copying program:</p>
                <p className="font-medium">{programToCopy?.name}</p>
                {programToCopy?.location_name && (
                  <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                    <MapPin className="h-3 w-3" />
                    {programToCopy.location_name}
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Target Location *</Label>
                <Select value={copyTargetLocation} onValueChange={setCopyTargetLocation}>
                  <SelectTrigger data-testid="copy-target-location-select">
                    <SelectValue placeholder="Select target location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations
                      .filter(loc => loc.id !== programToCopy?.location_id)
                      .map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name} {loc.is_primary && '(Primary)'}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>New Program Name (optional)</Label>
                <Input
                  value={copyNewName}
                  onChange={(e) => setCopyNewName(e.target.value)}
                  placeholder={`${programToCopy?.name} (Location Name)`}
                  data-testid="copy-new-name-input"
                />
                <p className="text-xs text-slate-500">
                  Leave blank to auto-generate name with location
                </p>
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCopyDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCopyProgram} 
                disabled={copying || !copyTargetLocation}
                data-testid="confirm-copy-program-btn"
              >
                {copying ? 'Copying...' : 'Copy Program'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Programs;
